import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { api, IMAGES, CATEGORY_META } from "../lib/api";
import { MaskLine, Reveal } from "../components/Reveal";
import VehicleSelector from "../components/VehicleSelector";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";

const MANIFESTO = [
  { n: "01", t: "Fitment First", d: "Every build starts with the right stance. Our Shop-by-Vehicle engine simulates offset, bolt pattern and clearance so you order once and bolt on with confidence." },
  { n: "02", t: "Real Brands Only", d: "Fuel Off-Road, KMC, Moto Metal, Black Rhino and ReadyLIFT are live now — with Nitto, Toyo, Falken, ICON, Rough Country and more rolling in. Only names that actually earn their spot on the trail." },
  { n: "03", t: "Built To Abuse", d: "Rims, tires and suspension engineered for rock, sand and pavement. No accessories, no filler — just the hardware that changes how your rig moves." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

  useEffect(() => {
    api.get("/products", { params: { sort: "rating" } })
      .then((r) => setFeatured(r.data.slice(0, 8)))
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
          <img src={IMAGES.hero} alt="Lifted truck" className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-[#050505]/30" />
          <div className="absolute inset-0 bg-[#050505]/30" />
        </motion.div>

        <motion.div style={{ y: textY }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-10">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-xs uppercase tracking-[0.3em] text-[#00f0ff] mb-5"
          >
            Aftermarket Truck Parts — Rims · Tires · Suspension
          </motion.div>
          <h1 className="font-display text-6xl sm:text-8xl md:text-[10rem] leading-[0.85] uppercase">
            <MaskLine delay={0.15}>Unleash</MaskLine>
            <MaskLine delay={0.3} className="bttf-text">Your Rig</MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="text-zinc-300 max-w-lg mt-6 text-base md:text-lg"
          >
            Dial in the perfect offset, grip and lift. Pick your vehicle and we'll surface exactly what fits.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pb-16"
        >
          <VehicleSelector variant="hero" />
        </motion.div>
      </section>

      <Marquee />

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <h2 className="font-display text-5xl md:text-7xl uppercase leading-none">
            Three <span className="bttf-text">Disciplines</span>
          </h2>
          <p className="text-zinc-400 max-w-sm text-sm">Focused inventory. No lighting, no apparel, no filler — only what moves your truck.</p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {Object.entries(CATEGORY_META).map(([key, meta], i) => (
            <Reveal
              key={key}
              delay={i * 0.1}
            >
              <Link
                to={`/category/${key}`}
                data-testid={`category-tile-${key}`}
                className="group relative block h-[340px] md:h-[440px] overflow-hidden border border-[#27272a] card-glow transition-shadow"
              >
                <img src={meta.image} alt={meta.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#00f0ff] mb-2">0{i + 1} / Shop</span>
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-4xl md:text-6xl uppercase leading-none">{meta.label}</h3>
                    <ArrowUpRight className="text-white group-hover:text-[#00f0ff] transition-colors" size={36} />
                  </div>
                  <p className="text-zinc-300 mt-2 text-sm">{meta.tagline}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="border-y border-[#27272a] bg-[#080808]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-[#00f0ff]">The Extreme Offset Ethos</span>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-10 md:gap-16 mt-12">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.12}>
                <div className="font-display text-7xl text-stroke leading-none mb-4">{m.n}</div>
                <h3 className="font-display text-3xl uppercase mb-3">{m.t}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{m.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <h2 className="font-display text-5xl md:text-7xl uppercase leading-none">Trail-Rated <span className="bttf-text">Picks</span></h2>
          <Link to="/category/rims" className="group flex items-center gap-2 text-sm uppercase tracking-widest text-zinc-300 hover:text-[#00f0ff]">
            View All <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-[#27272a]">
        <img src={IMAGES.desert} alt="Truck in desert" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#050505]/75" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 text-center">
          <Reveal>
            <h2 className="font-display text-5xl md:text-8xl uppercase leading-none">Ready to <span className="bttf-text">Build?</span></h2>
            <p className="text-zinc-300 max-w-md mx-auto mt-5">Run the numbers with our offset and tire tools, then find parts that fit your exact rig.</p>
            <div className="flex flex-wrap gap-4 justify-center mt-8">
              <Link to="/tools" data-testid="cta-tools" className="bttf-gradient text-black font-bold uppercase tracking-wider px-8 py-4 hover:opacity-90 transition-opacity">Open Calculators</Link>
              <Link to="/brands" data-testid="cta-brands" className="border border-[#00f0ff] text-[#00f0ff] font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#00f0ff] hover:text-black transition-colors">Browse Brands</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
